import React from 'react';
import orderBy from 'lodash/orderBy';
import type { Candidate, CandidateEngagementDetails } from '@axiom/validation';
import {
  Brief,
  Button,
  Checkbox,
  CondensedHeader,
  Dropdown,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  ReadonlyTextarea,
  SectionHeader,
  SmallHeader,
  Textarea,
  useApi,
  Well,
} from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';

import { CandidateOpportunitiesApi } from '../../api/candidate-opportunities-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { SubmissionDetails } from '../SubmissionDetails/SubmissionDetails';
import { WarmingModalFormSchema } from '../../schema/warming-modal-form-schema';
import { WarmingModalUtil } from '../../utils/warming-modal-util';

export type WarmingModalProps = {
  candidate: Candidate;
  engagement: CandidateEngagementDetails;
  name?: string;
  onAfterSubmit: () => Promise<void>;
  onClose: () => void;
  submitButtonText?: string;
};

export const WarmingModal = ({
  candidate,
  engagement,
  name = 'WARMING_MODAL',
  onAfterSubmit,
  onClose,
  submitButtonText = 'Submit',
}: WarmingModalProps) => {
  const [{ data: experiences }] = useApi(
    CandidateExperienceApi.readExperiences(candidate.id)
  );

  const orderedExperiences = orderBy(
    experiences,
    ['endDate', 'startDate', 'isAxiom'],
    ['desc', 'desc', 'desc']
  );

  const initialValues = {
    candidateAvailabilityNotes: null,
    candidateExperienceNotes: null,
    candidateExploringOutsideRoles: null,
    candidatePlannedTimeOffNotes: null,
    experienceIds: orderedExperiences.map(e => e.id),
    hasNoConflictOfInterest: undefined,
  };

  const shouldTransitionIntoWarmedYes = () => {
    return [CandidateOpportunitiesConst.CandidateStatuses.Warmed].includes(
      engagement.candidateStatus
    );
  };

  return (
    <Form
      name={`${name}_FORM`}
      schema={WarmingModalFormSchema}
      initialValues={initialValues}
      onSubmit={async formData => {
        await CandidateOpportunitiesApi.updateWarming({
          candidateId: engagement.candidate.id,
          opportunityId: engagement.opportunity.id,
          body: {
            candidateAvailabilityNotes:
              formData.candidateAvailabilityNotes ??
              initialValues.candidateAvailabilityNotes,
            candidateExperienceNotes:
              formData.candidateExperienceNotes ??
              initialValues.candidateExperienceNotes,
            candidateExploringOutsideRoles:
              formData.candidateExploringOutsideRoles ??
              initialValues.candidateExploringOutsideRoles,
            candidatePlannedTimeOffNotes:
              formData.candidatePlannedTimeOffNotes ??
              initialValues.candidatePlannedTimeOffNotes,
            candidateStatus: shouldTransitionIntoWarmedYes()
              ? CandidateOpportunitiesConst.CandidateStatuses.WarmedYes
              : undefined,
            experiences: (
              formData.experienceIds || initialValues.experienceIds
            ).map((id: string) => ({
              id,
              // It's a trap!
              // . . . . . . . . . . . . . . . . _,,,--~~~~~~~~--,_
              // . . . . . . . . . . . . . . ,-' : : : :::: :::: :: : : : : :º '-, IT'S A TRAP!
              // . . . . . . . . . . . . .,-' :: : : :::: :::: :::: :::: : : :o : '-,
              // . . . . . . . . . . . ,-' :: ::: :: : : :: :::: :::: :: : : : : :O '-,
              // . . . . . . . . . .,-' : :: :: :: :: :: : : : : : , : : :º :::: :::: ::';
              // . . . . . . . . .,-' / / : :: :: :: :: : : :::: :::-, ;; ;; ;; ;; ;; ;; ;\
              // . . . . . . . . /,-',' :: : : : : : : : : :: :: :: : '-, ;; ;; ;; ;; ;; ;;|
              // . . . . . . . /,',-' :: :: :: :: :: :: :: : ::_,-~~,_'-, ;; ;; ;; ;; |
              // . . . . . _/ :,' :/ :: :: :: : : :: :: _,-'/ : ,-';'-'''''~-, ;; ;; ;;,'
              // . . . ,-' / : : : : : : ,-''' : : :,--'' :|| /,-'-'--'''__,''' \ ;; ;,-'/
              // . . . \ :/,, : : : _,-' --,,_ : : \ :\ ||/ /,-'-'x### ::\ \ ;;/
              // . . . . \/ /---'''' : \ #\ : :\ : : \ :\ \| | : (O##º : :/ /-''
              // . . . . /,'____ : :\ '-#\ : \, : :\ :\ \ \ : '-,___,-',-`-,,
              // . . . . ' ) : : : :''''--,,--,,,,,,¯ \ \ :: ::--,,_''-,,'''¯ :'- :'-,
              // . . . . .) : : : : : : ,, : ''''~~~~' \ :: :: :: :'''''¯ :: ,-' :,/\
              // . . . . .\,/ /|\\| | :/ / : : : : : : : ,'-, :: :: :: :: ::,--'' :,-' \ \
              // . . . . .\\'|\\ \|/ '/ / :: :_--,, : , | )'; :: :: :: :,-'' : ,-' : : :\ \,
              // . . . ./¯ :| \ |\ : |/\ :: ::----, :\/ :|/ :: :: ,-'' : :,-' : : : : : : ''-,,
              // . . ..| : : :/ ''-(, :: :: :: '''''~,,,,,'' :: ,-'' : :,-' : : : : : : : : :,-'''\\
              // . ,-' : : : | : : '') : : :¯''''~-,: : ,--''' : :,-'' : : : : : : : : : ,-' :¯'''''-,_ .
              // ./ : : : : :'-, :: | :: :: :: _,,-''''¯ : ,--'' : : : : : : : : : : : / : : : : : : :''-,
              // / : : : : : -, :¯'''''''''''¯ : : _,,-~'' : : : : : : : : : : : : : :| : : : : : : : : :
              // : : : : : : : :¯''~~~~~~''' : : : : : : : : : : : : : : : : : : | : : : : : : : : :
              // if we decide to set isHighlighted: true here someday, make sure
              // to limit it to the Warmed status only, otherwise it can override
              // a customization TS has made to the submission
            })),
            hasNoConflictOfInterest:
              formData.hasNoConflictOfInterest ??
              initialValues.hasNoConflictOfInterest,
          },
        });

        return onAfterSubmit();
      }}
    >
      {({ fireSubmit }) => (
        <Modal name={name} size="large">
          <ModalHeader
            onClose={() => {
              onClose();
            }}
            name="WARMING_MODAL_HEADER"
          >
            Complete next steps for this opportunity
          </ModalHeader>
          <ModalSection>
            <Grid>
              <GridRow>
                <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
                  <Well background="blue">
                    <SectionHeader>Share a few details</SectionHeader>
                    <Gutter bottom="8px" />
                    <Paragraph>
                      Confirm basic qualifications and availability for this
                      opportunity.
                    </Paragraph>
                  </Well>
                  <Gutter bottom="24px" />
                  <SectionHeader>Select relevant experience</SectionHeader>
                  <Gutter bottom="16px" />
                  <FormGroup
                    name="experienceIds"
                    label="Select each experience that makes you a good match for this opportunity"
                    description="Please make sure that each experience you select is accurate and up-to-date in your bio"
                  >
                    <Layout verticalGutter="8px" direction="vertical">
                      {orderedExperiences.map(experience => (
                        <Checkbox
                          key={experience.id}
                          name="experienceIds"
                          displayValue={WarmingModalUtil.getExperienceLabel(
                            experience
                          )}
                          option={experience.id}
                        />
                      ))}
                    </Layout>
                  </FormGroup>
                  <Gutter bottom="16px" />
                  <Textarea
                    name="candidateExperienceNotes"
                    label="Is there anything specific in your experience that you'd like us to highlight with the client?"
                  />
                  <Gutter bottom="24px" />
                  <SectionHeader>Tell us more</SectionHeader>
                  <Dropdown
                    displayKey="label"
                    name="candidateExploringOutsideRoles"
                    options={[
                      'Yes, I am actively seeking other roles',
                      'Yes, I am currently interviewing for another role',
                      'No, not at this time',
                    ].map(v => ({ value: v, label: v }))}
                    valueKey="value"
                    label="Are you currently exploring other opportunities outside of Axiom?"
                  />
                  <Gutter bottom="16px" />
                  <Textarea
                    name="candidatePlannedTimeOffNotes"
                    label="Do you have any extended time off planned over the next few months? If so, when exactly will you be unavailable?"
                  />
                  <Gutter bottom="16px" />
                  <Textarea
                    name="candidateAvailabilityNotes"
                    label="How soon would you be available to start this engagement?"
                  />
                  <Gutter bottom="24px" />
                  <SectionHeader>Conflict check</SectionHeader>
                  <Gutter bottom="16px" />
                  <Paragraph>
                    To opt in to the opportunity, you must certify that there is
                    no conflict of interest. If you’re not sure if something is
                    a conflict of interest and want to talk to us about it,
                    close this window, and reach out to your talent partner.
                  </Paragraph>
                  <Gutter bottom="16px" />
                  <Checkbox
                    name="hasNoConflictOfInterest"
                    displayValue="I certify that this opportunity does not present any actual or potential conflicts of interest that would prevent me from acting for the client on this matter."
                  />
                  <Gutter bottom="24px" only="smallScreen" />
                </GridColumn>
                <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
                  <SmallHeader>Job description</SmallHeader>
                  <Gutter bottom="16px" />
                  <SubmissionDetails
                    submission={engagement}
                    name="ENGAGEMENT_DETAILS"
                    stacked
                  />
                  <Gutter bottom="16px" />
                  <CondensedHeader>Work Description</CondensedHeader>
                  <Gutter bottom="4px" />
                  <Brief briefMaxHeight="96px">
                    <ReadonlyTextarea
                      value={engagement.position?.description || '--'}
                      name="WORK_DESCRIPTION"
                      formatting="modern"
                    />
                  </Brief>
                </GridColumn>
              </GridRow>
            </Grid>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL"
              onClick={() => {
                onClose();
              }}
              variation="outline"
            >
              Cancel
            </Button>
            <Button name="SUBMIT" onClick={fireSubmit}>
              {submitButtonText}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
